import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEYS = {
  admin: 'admin-dashboard-theme',
  vendor: 'vendor-dashboard-theme',
};

const DashboardThemeContext = createContext(null);

function readStoredTheme(variant) {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS[variant]);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyDocumentTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function DashboardThemeProvider({ variant, children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme(variant));

  useEffect(() => {
    applyDocumentTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEYS[variant], theme);
    } catch {
      /* ignore */
    }
  }, [theme, variant]);

  useEffect(() => {
    setThemeState(readStoredTheme(variant));
  }, [variant]);

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'dark' ? 'dark' : 'light');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      setTheme,
      toggleTheme,
      variant,
    }),
    [theme, setTheme, toggleTheme, variant]
  );

  return (
    <DashboardThemeContext.Provider value={value}>
      {children}
    </DashboardThemeContext.Provider>
  );
}

export function useDashboardTheme() {
  const ctx = useContext(DashboardThemeContext);
  if (!ctx) {
    throw new Error('useDashboardTheme must be used within DashboardThemeProvider');
  }
  return ctx;
}

export default DashboardThemeContext;
